import {BusinessECommerceStore, DomainVerificationStatus} from 'web-angular-client';
import {ECommerceStoreVerificationDisplay} from '../../interface/ecommerce-store-verification-display';
import {LivePaymentsBanner} from '../../interface/live-payments-banner';

/**
 * What to tell the merchant about a store's compliance check.
 * <p>
 * The status only. The reviewer's reason is internal and is deliberately not on the contract the merchant portal
 * reads. A store carrying no decision is still awaiting one: the checks predate some links, and a merchant reading
 * nothing at all would assume there was nothing to wait for.
 * <p>
 * A passed check is reported as exactly that, not as being able to trade. Trading also needs the store to be active
 * and the merchant's KYC to currently stand.
 *
 * @param {BusinessECommerceStore} businessECommerceStore - the store link to describe
 * @return {ECommerceStoreVerificationDisplay} the status text, styling and tooltip for that store
 */
export function getECommerceStoreVerification(
    businessECommerceStore: BusinessECommerceStore): ECommerceStoreVerificationDisplay
{
    if (businessECommerceStore?.verificationStatus === DomainVerificationStatus.VERIFIED)
    {
        return withStatusIcon({
            label: 'Verified',
            tone: 'success',
            pillIconClass: 'fas fa-circle-check',
            detail: 'This store\'s domain has been verified.'
        });
    }
    if (businessECommerceStore?.verificationStatus === DomainVerificationStatus.FAILED)
    {
        return withStatusIcon({
            label: 'Rejected',
            tone: 'danger',
            pillIconClass: 'fas fa-circle-xmark',
            detail: 'This store cannot take payments. Please contact support.'
        });
    }
    return withStatusIcon({
        label: 'Awaiting Review',
        tone: 'warning',
        pillIconClass: 'fas fa-hourglass-half',
        detail: 'We are verifying this store. It cannot take payments until verification is complete.'
    });
}

/**
 * Adds the standalone status icon, built from the pill's icon and tone so the two cannot drift apart.
 *
 * @param {Omit<ECommerceStoreVerificationDisplay, 'iconClass'>} display - the status without its standalone icon
 * @return {ECommerceStoreVerificationDisplay} the status with its standalone icon
 */
function withStatusIcon(
    display: Omit<ECommerceStoreVerificationDisplay, 'iconClass'>): ECommerceStoreVerificationDisplay
{
    return {...display, iconClass: `${display.pillIconClass} text-${display.tone}`};
}

/**
 * Builds the live-payments banner for the selected store. With no store selected the merchant is looking at all of
 * them at once, so the go-live prompt stands rather than singling one out.
 *
 * @param {BusinessECommerceStore | null} selectedStore - the store the dashboard is showing, or null for all stores
 * @param {string} verificationSla - how long a check takes, as told to the merchant
 * @return {LivePaymentsBanner} the banner to render
 */
export function buildLivePaymentsBanner(
    selectedStore: BusinessECommerceStore | null,
    verificationSla: string): LivePaymentsBanner
{
    const ready: LivePaymentsBanner = {
        tone: 'ready',
        title: 'Sandbox and live payments setup',
        subtitle: 'Setup and test your store in sandbox mode, then add your live keys and switch to production. Our setup '
            + 'guide takes you through each step.',
        icon: 'fas fa-list-check',
        showSetupGuide: true
    };

    if (!selectedStore)
    {
        return ready;
    }

    const storeLabel = selectedStore.domain || 'this store';

    if (selectedStore.verificationStatus === DomainVerificationStatus.FAILED)
    {
        return {
            tone: 'rejected',
            title: `We couldn't verify ${storeLabel}`,
            subtitle: 'This store can\'t take live payments. Log a ticket and we\'ll sort it out.',
            icon: 'fas fa-triangle-exclamation',
            showSetupGuide: false
        };
    }

    if (selectedStore.verificationStatus !== DomainVerificationStatus.VERIFIED)
    {
        return {
            tone: 'pending',
            title: `We're verifying ${storeLabel}`,
            subtitle: `Your store can't take live payments until this check is done. Usually within ${verificationSla}.`,
            icon: 'fas fa-hourglass-half',
            showSetupGuide: true
        };
    }

    return ready;
}
